/**
 * IntegrationsConnectList — connect/connected grid for every integration a
 * deployed pipeline uses (required AND optional).
 *
 * Renders the EXACT same surface as the marketplace detail modal's
 * "Integrations" section — same markup + same `mkt-modal-integration*` CSS
 * classes (a 2-column grid of brand rows) — so the two look identical. We import
 * the modal's stylesheet here so the shared classes are present on the pipeline
 * page too, and reuse the same atoms/engine:
 *   - <IntegrationLogo>        — per-provider brand mark atom.
 *   - useIntegrationConnect()  — the shared Connect engine (OAuth popups +
 *                                Gitlab/Lark form modals + refetch-on-complete).
 *   - GET /workflows/{uuid}/integrations/status — per-workflow status (same
 *                                shape as the marketplace integration-status),
 *                                so the list is fully dynamic for whatever a
 *                                template declares (slack/lark/notion/jira…).
 *
 * Status shape: { required, connected, missing, optional, optionalConnected,
 * optionalMissing, integrations }. Entries are a provider-id string or an
 * `{ any: [...] }` OR-group; `integrations[id]` = { id, name, connectPath }.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import { checkmarkCircleOutline } from 'ionicons/icons';
import api from '../../../services/api';
import IntegrationLogo from '../IntegrationLogo';
import useIntegrationConnect from '../../../hooks/useIntegrationConnect';
// Reuse the marketplace modal's integration styles verbatim (2-col grid + row
// cards). CRA bundles CSS globally + dedupes, so this just guarantees the
// shared classes are loaded on the pipeline detail page.
import '../../../pages/MarketplacePage/MarketplaceDetailModal.css';
import './IntegrationsConnectList.css';

const IntegrationsConnectList = ({ workflowUuid }) => {
  const { refreshKey, handleConnectClick, connectModals } = useIntegrationConnect();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!workflowUuid) {
      setStatus(null);
      return undefined;
    }
    let cancelled = false;
    if (refreshKey === 0) setLoading(true);
    setError(null);
    api
      .get(`/workflows/${encodeURIComponent(workflowUuid)}/integrations/status`)
      .then((res) => { if (!cancelled) setStatus(res || null); })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Failed to load integration status'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workflowUuid, refreshKey]);

  // Brand mark + name — identical to the modal's BrandLogo (shared atom).
  const BrandLogo = useCallback(({ id, name }) => (
    <span className="mkt-modal-integration-brand">
      <IntegrationLogo id={id} size={18} title={name} />
      <span className="mkt-modal-integration-name">{name}</span>
    </span>
  ), []);

  // One row — byte-for-byte the modal's renderIntegrationRow (single id +
  // `{ any: [...] }` OR-group), minus the deploy-gate "flash" (no deploy here).
  const renderRow = useCallback((entry, connectedList, { optionalSection }) => {
    if (!status) return null;
    const connectBtn = (id, meta, optional) => (
      <button
        type="button"
        className={optional
          ? 'mkt-modal-integration-connect mkt-modal-integration-connect--optional'
          : 'mkt-modal-integration-connect'}
        onClick={() => handleConnectClick(id, meta.connectPath)}
      >
        {optional ? 'Connect (optional)' : 'Connect'}
      </button>
    );
    const connectedIcon = (
      <IonIcon icon={checkmarkCircleOutline} className="mkt-modal-integration-connected-icon" aria-label="Connected" />
    );

    if (typeof entry === 'string') {
      const id = entry;
      const meta = status.integrations?.[id] || { id, name: id, connectPath: '/integrations' };
      const isConnected = (connectedList || []).includes(id);
      const rowCls = isConnected ? 'is-connected' : optionalSection ? 'is-optional' : 'is-missing';
      return (
        <li key={`${optionalSection ? 'opt' : 'req'}:${id}`} className={`mkt-modal-integration-row ${rowCls}`}>
          <BrandLogo id={id} name={meta.name} />
          <span className="mkt-modal-integration-state">
            {isConnected ? connectedIcon : connectBtn(id, meta, optionalSection)}
          </span>
        </li>
      );
    }
    // OR group — one row per member; once one satisfies, siblings read optional.
    const members = Array.isArray(entry?.any) ? entry.any : [];
    if (members.length === 0) return null;
    const matchedGroup = (connectedList || []).find(
      (c) => typeof c === 'object' && Array.isArray(c?.any) && c.any.join(',') === members.join(','),
    );
    const matched = matchedGroup?.connectedMembers || [];
    const groupSatisfied = matched.length > 0;
    return members.map((mid) => {
      const meta = status.integrations?.[mid] || { id: mid, name: mid, connectPath: '/integrations' };
      const isThisConnected = matched.includes(mid);
      const rowCls = isThisConnected ? 'is-connected' : (groupSatisfied || optionalSection) ? 'is-optional' : 'is-missing';
      const optional = optionalSection || groupSatisfied;
      return (
        <li key={`${optionalSection ? 'opt' : 'req'}:${members.join(',')}:${mid}`} className={`mkt-modal-integration-row ${rowCls}`}>
          <BrandLogo id={mid} name={meta.name} />
          <span className="mkt-modal-integration-state">
            {isThisConnected ? connectedIcon : connectBtn(mid, meta, optional)}
          </span>
        </li>
      );
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, handleConnectClick]);

  const hasRequired = status && Array.isArray(status.required) && status.required.length > 0;
  const hasOptional = status && Array.isArray(status.optional) && status.optional.length > 0;
  const hasAny = hasRequired || hasOptional;

  return (
    <>
      {loading ? (
        <div className="int-connect-loading">
          <IonSpinner name="dots" />
          <span>Loading integrations…</span>
        </div>
      ) : error ? (
        <p className="int-connect-error">
          Couldn’t load integration status. You can manage integrations from{' '}
          <a href="/integrations">Integrations</a>.
        </p>
      ) : hasAny ? (
        <ul className="mkt-modal-integrations">
          {hasRequired && status.required.map((entry) =>
            renderRow(entry, status.connected, { optionalSection: false }))}
          {hasOptional && status.optional.map((entry) =>
            renderRow(entry, status.optionalConnected, { optionalSection: true }))}
        </ul>
      ) : (
        <p className="int-connect-none">No integrations required by this agent.</p>
      )}
      {connectModals}
    </>
  );
};

export default IntegrationsConnectList;
